import { useState } from "react";
import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import {
  Shield, MapPin, CheckCircle2, Clock, Users, Building2,
  ArrowRight, Lock, Phone, Receipt, Star, Search, Zap, MessageSquare, HelpCircle,
} from "lucide-react";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Brand from "@/components/ui/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import heroSucheBg from "@/assets/hero-suche-bg.jpg";

export default function Suche() {
  const { t } = useTranslation();
  const [showHow, setShowHow] = useState(false);

  const trustBadges = [
    t("suche.trustDach"),
    t("suche.trustFree"),
    t("suche.trustGdpr"),
    t("suche.trustConsent"),
  ];

  const steps = [
    { number: "01", icon: MessageSquare, title: t("suche.step1Title"), desc: t("suche.step1Desc") },
    { number: "02", icon: Zap, title: t("suche.step2Title"), desc: t("suche.step2Desc") },
    { number: "03", icon: Users, title: t("suche.step3Title"), desc: t("suche.step3Desc") },
  ];

  const problems = [
    t("suche.problem1"), t("suche.problem2"), t("suche.problem3"),
    t("suche.problem4"), t("suche.problem5"), t("suche.problem6"),
  ];

  const whyPoints = [
    { icon: Receipt, text: t("suche.why1") },
    { icon: Shield, text: t("suche.why2") },
    { icon: Lock, text: t("suche.why3") },
    { icon: Users, text: t("suche.why4") },
    { icon: Clock, text: t("suche.why5") },
    { icon: Phone, text: t("suche.why6") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("suche.seoTitle")}
        description={t("suche.seoDesc")}
        canonical="/suche"
      />
      <JsonLd
        includeService
        includeFaq
        includeHowTo
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
          { name: t("footer.findCraft"), url: "https://www.craftfindus.com/suche" },
        ]}
      />

      <Header />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroSucheBg}
            alt={t("suche.seoDesc")}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 section-padding pb-20 md:pb-24 pt-24 md:pt-32 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-display mb-6 opacity-0 animate-fade-up">
              {t("suche.heroTitle")}{" "}
              <span className="text-accent">{t("suche.heroAccent")}</span>
            </h1>
            <p className="text-body text-muted-foreground max-w-2xl mx-auto mb-10 text-lg opacity-0 animate-fade-up delay-100">
              {t("suche.heroDesc")}
            </p>

            <div className="flex items-center justify-center mb-4 opacity-0 animate-fade-up delay-200">
              <LocaleLink
                to="/inquiry"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-medium text-base hover:bg-primary/90 transition-colors"
              >
                {t("suche.heroCta")}
                <ArrowRight className="w-4 h-4" />
              </LocaleLink>
            </div>
            <p className="text-micro mb-12 opacity-0 animate-fade-up delay-300">{t("suche.heroSubline")}</p>

            <div className="flex flex-wrap items-center justify-center gap-4 opacity-0 animate-fade-up delay-400">
              {trustBadges.map((badge) => (
                <span key={badge} className="text-micro px-3 py-1.5 border border-border bg-card">
                  {badge}
                </span>
              ))}
            </div>

            <button
              onClick={() => setShowHow(true)}
              className="mt-6 inline-flex items-center gap-1.5 text-caption hover:text-foreground transition-colors opacity-0 animate-fade-up delay-500"
            >
              <HelpCircle className="w-4 h-4" />
              {t("suche.howBtn", { brand: "CRAFTFINDUS" })}
            </button>

            <Dialog open={showHow} onOpenChange={setShowHow}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-subheadline">{t("suche.howTitle", { brand: "CRAFTFINDUS" })}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-body text-muted-foreground">
                  <p>{t("suche.howIntro", { brand: "CRAFTFINDUS" })}</p>
                  <ol className="list-decimal list-inside space-y-2">
                    <li><strong>{t("suche.howStep1")}</strong> – {t("suche.howStep1Desc")}</li>
                    <li><strong>{t("suche.howStep2")}</strong> – {t("suche.howStep2Desc")}</li>
                    <li><strong>{t("suche.howStep3")}</strong> – {t("suche.howStep3Desc")}</li>
                    <li><strong>{t("suche.howStep4")}</strong> – {t("suche.howStep4Desc")}</li>
                  </ol>
                  <div className="p-4 bg-accent/5 border border-accent/20">
                    <p className="text-body text-foreground font-medium mb-1">{t("suche.howFreeTitle")}</p>
                    <p className="text-caption">{t("suche.howFreeDesc")}</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 opacity-0 animate-fade-up delay-500 hidden md:block">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-muted-foreground/40 mx-auto" />
        </div>
      </section>

      {/* ── SECTION 2 – Typische Projekte ─────────────────── */}
      <section className="py-20 md:py-28 section-padding bg-card border-y border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-headline text-center mb-4">
            {t("suche.problemsTitle")} <span className="text-primary">{t("suche.problemsAccent")}</span>{t("suche.problemsSuffix")}
          </h2>
          <p className="text-center text-caption mb-16">{t("suche.problemsDesc")}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {problems.map((problem) => (
              <div key={problem} className="flex items-start gap-3 p-5 border border-border bg-background">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-body">{problem}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <LocaleLink
              to="/inquiry"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-foreground transition-colors"
            >
              {t("suche.problemsCta")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
            <p className="text-micro mt-2">{t("suche.problemsCtaSub")}</p>
          </div>
        </div>
      </section>

      {/* ── SECTION 3 – So funktioniert's ─────────────────── */}
      <section className="py-20 md:py-28 section-padding">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-headline text-center mb-4">
            {t("suche.stepsTitle")} <span className="text-primary">{t("suche.stepsAccent")}</span>
          </h2>
          <p className="text-center text-caption mb-16">{t("suche.stepsDesc", { brand: "CRAFTFINDUS" })}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.number} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-medium text-primary/20" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.number}
                    </span>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-subheadline">{step.title}</h3>
                  <p className="text-caption leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 border border-border px-6 py-4 text-center">
            <p className="text-sm font-medium tracking-wide uppercase text-accent">
              {t("suche.stepsBanner")}
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4 – Beispiel-Anfrage ──────────────────── */}
      <section className="py-20 md:py-28 section-padding bg-card border-y border-border">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-headline text-center mb-12">
            {t("suche.exampleTitle")} <span className="text-primary">{t("suche.exampleAccent")}</span>{t("suche.exampleSuffix")}
          </h2>

          <div className="border border-border bg-background p-8">
            <div className="flex items-center gap-3 mb-6">
              <Search className="w-5 h-5 text-primary" />
              <span className="text-micro">{t("suche.exampleLabel")}</span>
            </div>

            <h3 className="text-subheadline mb-6">{t("suche.exampleName")}</h3>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="space-y-1">
                <span className="text-micro flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t("suche.regionLabel")}</span>
                <p className="text-sm text-foreground">{t("suche.exampleRegion")}</p>
              </div>
              <div className="space-y-1">
                <span className="text-micro flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {t("suche.objectLabel")}</span>
                <p className="text-sm text-foreground">{t("suche.exampleObject")}</p>
              </div>
              <div className="space-y-1">
                <span className="text-micro flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t("suche.timeframeLabel")}</span>
                <p className="text-sm text-foreground">{t("suche.exampleUrgency")}</p>
              </div>
              <div className="space-y-1">
                <span className="text-micro flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {t("suche.resultLabel")}</span>
                <p className="text-sm text-accent font-medium">{t("suche.exampleMatched")}</p>
              </div>
            </div>

            <div className="divider-line my-6" />
            <p className="text-caption text-center" dangerouslySetInnerHTML={{ __html: t("suche.exampleResult") }} />
          </div>

          <div className="text-center mt-8">
            <LocaleLink
              to="/inquiry"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t("suche.exampleCta")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* ── SECTION 5 – Warum CRAFTFINDUS ────────────────────── */}
      <section className="py-20 md:py-28 section-padding">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-headline text-center mb-16">
            {t("suche.whyTitle", { brand: "CRAFTFINDUS" })}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {whyPoints.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-4 p-5 border border-border bg-card">
                <Icon className="w-5 h-5 text-primary shrink-0" />
                <span className="text-body">{text}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-1 text-accent mt-12">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-5 h-5 fill-accent" />
            ))}
            <span className="text-caption ml-2">{t("suche.whyTrust")}</span>
          </div>
        </div>
      </section>

      {/* ── SECTION 6 – Finaler CTA ───────────────────────── */}
      <section className="py-24 md:py-32 section-padding bg-card border-y border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-headline mb-6">
            {t("suche.ctaTitle")} <span className="text-primary">{t("suche.ctaAccent")}</span>{t("suche.ctaSuffix")}
          </h2>
          <p className="text-body text-muted-foreground mb-10 text-lg whitespace-pre-line">
            {t("suche.ctaDesc")}
          </p>

          <LocaleLink
            to="/inquiry"
            className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground font-medium text-lg hover:bg-primary/90 transition-colors"
          >
            {t("suche.ctaBtn")}
            <ArrowRight className="w-5 h-5" />
          </LocaleLink>

          <p className="text-micro mt-6">{t("suche.ctaSubline")}</p>

          <div className="mt-10 p-6 border border-accent/30 bg-accent/5">
            <p className="text-lg font-medium text-accent flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              {t("suche.ctaPhone")}{" "}
              <a href="tel:+4921224355830" className="underline hover:text-foreground transition-colors">
                0212 24355830
              </a>
            </p>
          </div>

          <nav className="mt-12 flex flex-wrap items-center justify-center gap-6 text-caption" aria-label="Weiterführende Links">
            <LocaleLink to="/fuer-handwerker" className="link-subtle hover:text-foreground transition-colors">{t("suche.ctaForCraft")}</LocaleLink>
            <LocaleLink to="/leads" className="link-subtle hover:text-foreground transition-colors">{t("suche.ctaForLeads")}</LocaleLink>
            <LocaleLink to="/kontakt" className="link-subtle hover:text-foreground transition-colors">{t("suche.ctaContact")}</LocaleLink>
          </nav>
        </div>
      </section>

      <Footer />
    </div>
  );
}